import ButtonGroup from '@pelckmans/elementis/components/button-group';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import useIsMobile from '../../../../../hooks/useIsMobile';
import useStudioGroup from '../../../../../hooks/useStudioGroup';
import AppsContext from '../../../_contexts/AppsContext';
import ModuleContext from '../../../_contexts/ModuleContext';
import useDigibook from '../../../hooks/useDigibook';
import useSlideSets from '../../../hooks/useSlideSets';
import AppQuickActionButton from './AppQuickActionButton';
import MobileAppsDropdownButton from './MobileAppsDropdownButton';
import SlideSetAppButton from './SlideSetAppButton';
import WordListAppButton from './WordListAppButton';
import useDigibookApps from './hooks/useDigibookApps';

export default function AppButtonList({
  density = 'normal',
  digibooksOnly = false,
  showDigibooks = true,
}: {
  density?: 'normal' | 'compact' | 'small';
  digibooksOnly?: boolean;
  showDigibooks?: boolean;
}) {
  const { selectedModule } = useContext(ModuleContext);
  const { apps } = useContext(AppsContext);

  const { t } = useTranslation();
  const { slideSets } = useSlideSets({ optional: digibooksOnly });
  const { digibook, pageNumber } = useDigibook();
  const studioGroup = useStudioGroup();

  const size = density === 'compact' ? 'sm' : 'md';
  const isMobile = useIsMobile();
  const isBAO = studioGroup === 'BAO';

  const { mobileDigibookItems, digibookButtons, hasDigibookApps } = useDigibookApps({
    apps,
    selectedModuleId: selectedModule?.id,
    showDigibooks,
    pageNumber,
    size,
  });

  if (!selectedModule) return null;

  // apps includes otherApps and wordLists, so we don't need to check those separately
  if (!digibook && !hasDigibookApps && apps.length === 0 && slideSets.length === 0) return null;

  const getButtons = () => {
    if (isBAO && isMobile) {
      return [
        <SlideSetAppButton key="slide-sets" size={size} />,
        <MobileAppsDropdownButton key="mobile-apps" size={size} digibooks={mobileDigibookItems} />,
      ];
    }

    if (isBAO && !isMobile) {
      return [
        <SlideSetAppButton key="slide-sets" size={size} />,
        ...digibookButtons,
        <WordListAppButton key="word-lists" size={size} />,
        <AppQuickActionButton key="apps-quick-action" size={size} />,
      ];
    }

    if (!isBAO && isMobile) {
      return [...digibookButtons, <MobileAppsDropdownButton key="mobile-apps" size={size} slideSets={slideSets} />];
    }
    if (!isBAO && !isMobile) {
      return [
        ...digibookButtons,
        <SlideSetAppButton key="slide-sets" size={size} />,
        <WordListAppButton key="word-lists" size={size} />,
        <AppQuickActionButton key="apps-quick-action" size={size} />,
      ];
    }
  };

  if (digibooksOnly) {
    return (
      <ButtonGroup role="group" aria-label={t('PAGES.TOC_LEVEL.APPS.A11Y_LABEL')} size={size}>
        {digibookButtons}
      </ButtonGroup>
    );
  }

  return (
    <ButtonGroup role="group" aria-label={t('PAGES.TOC_LEVEL.APPS.A11Y_LABEL')} size={size}>
      {getButtons()}
    </ButtonGroup>
  );
}
